import { roundNumber } from '../round-number/'
import Moment from 'moment/moment'
import momentTz from 'moment-timezone'
import toNumber from 'lodash/toNumber'
import lowerCase from 'lodash/lowerCase'
import type { DateType } from '@ztechapps/common.contract'

export function mmToInch(mm = 0, decimalPlace = 4) {
  return normalizedResult(mm / 25.4, decimalPlace)
}

//converting from inches to mm needs to be 2 decimal places, only as it is difficult to it
export function inchToMm(inch = 0, decimalPlace = 2) {
  return normalizedResult(inch * 25.4, decimalPlace)
}

export function poundsToOunce(pounds = 0, decimalPlace = 4) {
  return normalizedResult(pounds * 16, decimalPlace)
}

export function ounceToPounds(ounce = 0, decimalPlace = 4) {
  return normalizedResult(ounce / 16, decimalPlace)
}

export function gramsToOunce(grams = 0, decimalPlace = 6) {
  return normalizedResult(grams * 0.035274, decimalPlace)
}

export function gramsToPounds(grams = 0, decimalPlace = 8) {
  return normalizedResult(grams * 0.00220462, decimalPlace)
}

const normalizedResult = (result: number, decimalPlace: number) => {
  const finalResult = roundNumber(result, decimalPlace)

  //if isNan, just return zero, the number may be too small to be considered
  return isNaN(finalResult) ? 0 : finalResult
}

/**
 * This will convert multiple units to ounce, some shipping weights supports only ounce so we need this general convertion tool
 * @param shippingWeight
 * @param unit
 */
export function shippingWeightToOunce(shippingWeight = 0, unit = '') {
  const weightValue = toNumber(shippingWeight ?? 0)
  switch (lowerCase(unit ?? '')) {
    case 'lbs':
    case 'pounds':
    case 'pound':
      return poundsToOunce(weightValue)

    case 'kg':
    case 'kilograms':
    case 'kilogram':
      //convert first to grams then to pounds
      return gramsToOunce(weightValue * 1000)

    case 'g':
    case 'grams':
    case 'gram':
      //convert first to grams then to pounds
      return gramsToOunce(weightValue)

    //fallback to ounce
    default:
      return weightValue
  }
}

/**
 * Converts a given date to a Moment object.
 * If no date is provided, current date is used.
 * @param date - The date to be converted
 * @param timeZone - The time zone to be used for conversion
 * @param options - Additional options for the conversion
 * @param options.keepLocalTime - If true, keeps the local time
 * @returns The Moment object representing the converted date
 */
export const toDate = (
  date?: DateType,
  timeZone?: string,
  options?: {
    keepLocalTime?: boolean
  }
): Moment.Moment => {
  //if the date is type of null or empty string, it should always be current date
  let momentObj = Moment(date)

  //if the date is number is should be unix timestamp
  if (typeof date === 'number') {
    momentObj = Moment(date * 1000)
  }

  //when timezone is included, use the timezone offset
  if (timeZone) {
    return momentTz(momentObj).tz(timeZone, options?.keepLocalTime)
  }
  return momentObj
}

export const timeFromNow = (
  date: DateType,
  timeZone?: string,
  options?: {
    keepLocalTime?: boolean
  }
) => {
  return toDate(date, timeZone, options).fromNow()
}
